// Startup gate: fetches `/settings.json` (deployment-time config injected by the
// SPA's CDK stack), populates the `configs` module, then triggers Metabase's
// site-settings load. Children render only after both have completed — so no
// component reads `getConfigSettings()` before it's populated.
//
// The gate holds only for that first load. Later `refreshSiteSettings` calls
// (every user-setting save triggers one) must not unmount the app: the remount
// re-runs mount-time effects, and the native editor's database auto-select
// then saves `last-used-native-database-id` again, looping forever.

import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { GenericError } from "metabase/common/components/ErrorPages";
import { useDispatch, useSelector } from "metabase/redux/hooks";
import { refreshSiteSettings } from "metabase/redux/settings";
import { setBasename } from "metabase/utils/basename";

import { type AppSettings, setConfigSettings } from "./configs";
import { appSettingsLoaded, getIsAppSettingsLoaded } from "./state";

export const AppSettingsGetter = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const [error, setError] = useState<unknown>(null);
  const [hasLoadedSiteSettings, setHasLoadedSiteSettings] = useState(false);
  const isAppLoaded = useSelector(getIsAppSettingsLoaded);

  useEffect(() => {
    const run = async () => {
      try {
        const result = await fetch("/settings.json");
        if (result.ok) {
          const settings: AppSettings = await result.json();
          setConfigSettings(settings);
          // Route all subsequent API calls at the Fargate backend origin.
          // Upstream's `ApiClient.buildUrl` concatenates this in front of every
          // relative path; when it's an absolute URL, `new URL(...)` treats it
          // as the origin so `location.origin` (the CloudFront SPA host) is
          // ignored. `refreshSiteSettings` below is the first call that lands
          // on the new base, so we must set it before dispatching that thunk.
          setBasename(settings.insightsApiBaseUrl);
        }
        dispatch(appSettingsLoaded(true));
        // `.unwrap()` so a rejected thunk (network error, non-2xx) throws
        // into our catch. Also treat an empty payload as a failure — without
        // real settings, upstream's routing thinks the instance isn't set up
        // and would send the user to `/setup`, which isn't reachable in the
        // IAS deployment (no anonymous setup flow — SSO is the only way in).
        //
        // Exception: a 401 means "not logged in yet" — that's the normal
        // pre-SSO state and `AppSsoProvider` will bounce the user through
        // the OIDC redirect. Let it through instead of covering it with the
        // generic error page.
        try {
          const settings = await dispatch(refreshSiteSettings()).unwrap();
          if (!settings || Object.keys(settings).length === 0) {
            throw new Error("Empty site-settings payload");
          }
        } catch (rtkErr) {
          const status = (rtkErr as { status?: number } | null)?.status;
          if (status !== 401) {
            throw rtkErr;
          }
        }
        setHasLoadedSiteSettings(true);
      } catch (err) {
        setError(err);
        console.error("AppSettingsGetter: error loading settings", err);
      }
    };
    void run();
  }, [dispatch]);

  if (error) {
    return <GenericError details={String(error)} />;
  }
  if (!isAppLoaded || !hasLoadedSiteSettings) {
    return null;
  }
  return children;
};
